import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import { CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { ToggleService } from '../header/toggle.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { RouterModule } from '@angular/router';
import {FeathericonsModule} from "../../icons/feathericons/feathericons.module";
import { TranslateModule } from '@ngx-translate/core';
import { AuthorizationService } from '../../core/services/authorization.service';

// Interfaz para los ítems del menú
export interface MenuItem {
    type: 'link' | 'submenu' | 'subtitle'; // Tipo de ítem
    title: string;                         // Texto a mostrar
    icon?: string;                          // Nombre del ícono (ej. Feather icons)
    iconType?: string;                          // Nombre del ícono (ej. Feather icons)
    routerLink?: string;                    // Ruta para Angular Router
    href?: string;                          // URL para enlaces externos o javascript:void(0)
    isExternalLink?: boolean;               // Indica si es un enlace externo
    badge?: string | number;                // Para mostrar badges como "Hot" o "5"
    exact?: boolean;                        // Para routerLinkActiveOptions {exact: true}
    children?: MenuItem[];                  // Array de sub-ítems (para 'submenu')
    panelOpenState?: boolean;               // Estado de expansión para 'submenu'
}

@Component({
    selector: 'app-sidebar',
    imports: [
        CommonModule,
        RouterModule,
        MatExpansionModule,
        NgScrollbarModule,
        FeathericonsModule,
        TranslateModule
    ],
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit {
    @Output() toggleSidebar = new EventEmitter<void>();
    public menuItems: MenuItem[] = [];

    constructor(
        private toggleService: ToggleService,
        private authorizationService: AuthorizationService
    ) {
        this.toggleService.isToggled$.subscribe(isToggled => {
            this.isToggled = isToggled;
        });
    }

    getModuleFromRoute(routerLink: string | undefined): string {
        if (!routerLink) return '';
        
        // Mapear rutas a módulos
        const routeToModuleMap: { [key: string]: string } = {
            '/user': 'user',
            '/driver': 'driver',
            '/client': 'client',
            '/freight': 'freight',
            '/invoice': 'invoice',
            '/invoice/billing': 'invoice',
            '/bill': 'bill',
            '/cash-movement': 'cash-movement',
            '/income': 'income',
            '/expense': 'expense',
            '/payment-method': 'payment-method',
            '/cash-movement-concept': 'cash-movement-concept',
            '/configuration': 'configuration'
        };
        
        return routeToModuleMap[routerLink] || '';
    }

    hasModuleAccess(module: string): boolean {
        if (!module) return true; // Si no hay módulo, mostrar (para subtítulos)
        return this.authorizationService.hasModuleAccess(module);
    }

    shouldShowSubtitle(subtitleIndex: number): boolean {
        // Buscar el siguiente subtítulo o el final del array
        const nextSubtitleIndex = this.menuItems.findIndex((item, index) => 
            index > subtitleIndex && item.type === 'subtitle'
        );
        
        const endIndex = nextSubtitleIndex === -1 ? this.menuItems.length : nextSubtitleIndex;
        
        // Verificar si hay al menos un enlace visible entre este subtítulo y el siguiente
        for (let i = subtitleIndex + 1; i < endIndex; i++) {
            const item = this.menuItems[i];
            if (item.type === 'link' && this.hasModuleAccess(this.getModuleFromRoute(item.routerLink))) {
                return true;
            }
        }
        
        return false;
    }

    ngOnInit(): void{
        this.menuItems = [
            // MAIN
            { type: 'subtitle', title: 'SIDEBAR.MAIN' },
            { type: 'link', title: 'SIDEBAR.USER', icon: 'user', routerLink: '/user', exact: true },
            { type: 'link', title: 'SIDEBAR.CLIENT', icon: 'users', routerLink: '/client', exact: true },
            { type: 'link', title: 'SIDEBAR.DRIVER', icon: 'truck', routerLink: '/driver', exact: true },
            { type: 'link', title: 'SIDEBAR.FREIGHT', icon: 'truck', routerLink: '/freight', exact: true },
            { type: 'link', title: 'SIDEBAR.INVOICE', icon: 'file-text', routerLink: '/invoice', exact: true },
            { type: 'link', title: 'SIDEBAR.FREIGHT_BILLING', icon: 'file-text', routerLink: '/invoice/billing', exact: true },
            // { type: 'link', title: 'SIDEBAR.BILL', icon: 'file-text', routerLink: '/bill', exact: true },
            // { type: 'link', title: 'SIDEBAR.CASH_BOX', icon: 'database', routerLink: '/cash-box', exact: true },
            // { type: 'subtitle', title: 'SIDEBAR.CASH_BOX' },

            // { type: 'link', title: 'SIDEBAR.CASH_MOVEMENT', icon: 'trending-up', routerLink: '/cash-movement', exact: true },
            // { type: 'link', title: 'SIDEBAR.INCOME', icon: 'trending-up', routerLink: '/income', exact: true },
            // { type: 'link', title: 'SIDEBAR.EXPENSE', icon: 'trending-down', routerLink: '/expense', exact: true },
            // { type: 'subtitle', title: 'SIDEBAR.BOX_CONFIGURATIONS' },

            // { type: 'link', title: 'SIDEBAR.PAYMENT_METHOD', icon: 'credit-card', routerLink: '/payment-method', exact: true },
            // { type: 'link', title: 'SIDEBAR.CASH_MOVEMENT_CONCEPT', icon: 'tag', routerLink: '/cash-movement-concept', exact: true },
            // { type: 'subtitle', title: 'SIDEBAR.CONFIGURATIONS' },
            // { type: 'link', title: 'SIDEBAR.CONFIGURATION', icon: 'settings', routerLink: '/configuration', exact: true },
        ];
    }

    // Toggle Service
    isToggled = false;
    toggle() {
        this.toggleService.toggle();
    }

    // Mat Expansion
    panelOpenState = false;


    // Función para prevenir la navegación en enlaces javascript:void(0) y permitir que routerLink funcione
    handleLinkClick(event: MouseEvent, item: MenuItem): void {
        if (item.isExternalLink && item.href === 'javascript:void(0);') {
            event.preventDefault();
        }
        // Si es un submenu y no tiene routerLink, prevenimos que el panel se cierre/abra si el click fue en el header para expandir/colapsar
        // Esto es manejado por mat-expansion-panel directamente.
    }

    // Métodos para verificar rol principal
    getPrimaryRole(): string | null {
        return this.authorizationService.getPrimaryRole();
    }

    isPrimaryRoleSuperAdmin(): boolean {
        return this.authorizationService.isPrimaryRoleSuperAdmin();
    }

    isPrimaryRoleAdmin(): boolean {
        return this.authorizationService.isPrimaryRoleAdmin();
    }

    isPrimaryRoleTreasurer(): boolean {
        return this.authorizationService.isPrimaryRoleTreasurer();
    }

    // Métodos para verificar acceso a módulos
    canAccessModule(module: string): boolean {
        const userRoles = this.authorizationService.getUserRoles();
        
        // Si es Super Admin, tiene acceso a todo
        if (userRoles.includes('ROLE_SUPER_ADMIN')) {
            return true;
        }
        
        // Verificar permisos específicos para otros roles
        return this.authorizationService.hasModuleAccess(module);
    }
}
